import './menu_item_subpage.less';

import React, { Fragment } from 'react';

import MenuItemEditorGenerics from './menu_item_editor_generics.jsx';
import Checkbox from 'zoomforth_codesbane_editor/component_lib/checkbox.jsx';
import InputContainer from 'zoomforth_component_lib/input_container.jsx';
import TextInput from 'zoomforth_component_lib/basic_text_input.jsx';
import SubpageCssClassesInput from '../shared/subpage_css_classes_input.jsx';
import CopyToClipboardButton from 'zoomforth_component_lib/copy_to_clipboard_button.jsx';
import { CopyIcon } from 'zoomforth_component_lib/app_modern_icons.jsx';
import RadioButtonSet from 'zoomforth_component_lib/radio_button_functional_set.jsx';
import RadioButton from 'zoomforth_component_lib/radio_button_simple.jsx';

import TranslationsEditingDisabledDiv
  from 'zoomforth_codesbane_editor/component_lib/translations_editing_disabled_div_container.jsx';

export const SCROLL_INDICATOR_LIGHT = 'SCROLL_INDICATOR_LIGHT';
export const SCROLL_INDICATOR_DARK = 'SCROLL_INDICATOR_DARK';
export const DEFAULT_SCROLL_INDICATOR = SCROLL_INDICATOR_LIGHT;

const canChangeSubpageHiddenness = (isSubpageHidden, canHideSubpage)=>
  isSubpageHidden || // A hidden subpage can always be unhidden
  canHideSubpage; // Otherwise we must explicitly be allowed to hide the subpage

const MenuItemSubpage = ({
  subpageSlug,
  subpageUrl,
  subpageSlugError,
  scrollIndicatorColor,
  cssClassesList,
  canHideSubpage,
  canSubpageBeIndexed,
  isSubpageHidden,
  isMenuItemHidden,
  isScrollToTopEnabled,
  isScrollIndicatorEnabled,
  onCssClassesListChange,
  onIsSubpageHiddenChange,
  onSubpageSlugChange,
  onIsSubpageMenuItemHiddenChange,
  onIsScrollToTopEnabledChange,
  onIsScrollIndicatorEnabledChange,
  onScrollIndicatorColorChange,
  onCanSubpageBeIndexedChange,
  ...props
})=> (
  <span id="menu-item-subpage">
    <MenuItemEditorGenerics 
      isValid={!!props.title && !subpageSlugError}
      sidebarTitle={props.isNew ? 'Create new Subpage' : 'Edit Subpage'}
      titleInputName="Subpage Title"
      deleteText="Delete Subpage"
      saveText="Save"
      shouldShowCopyAndDuplicateBtns={!props.isNew}
      {...props}
    >
      <TranslationsEditingDisabledDiv>
        <InputContainer
          dark
          name="Subpage Slug"
          error={subpageSlugError}
          tooltip={
            <div>
              <p>The subpage slug is the final part of the URL that is unique for this subpage.</p>
              <p>Since URLs only support some letters, numbers and symbols we automatically format the text you enter into a valid URL.</p>
              <p>You will need to publish the page for slug changes to take effect.</p>
            </div>
          }
          autoComplete="off"
          
        >
          <div className="slug-input-container">
            <span className="slug-input-prefix">/</span>  
            <TextInput 
              dark
              className="subpage-slug-input"
              value={subpageSlug}
              onChange={(e) => onSubpageSlugChange(e.target.value) }
            />
            <CopyToClipboardButton 
              type="dark" 
              className="subpage-slug-button"
              successContent="Copied the URL to clipboard."
              disabled={!subpageUrl}
              disabledReason="No link exists for this site. Please publish the site to enable copying."              
              helpContent={<div>Click to copy: <a href='#'>{subpageUrl}</a></div>}
              data={subpageUrl}
              helpClassName="subpage-slug-tip"
              helpMouseEnterDelay={0}
              helpMouseLeaveDelay={0}
              helpPlacement="right"
            >
              <CopyIcon />
            </CopyToClipboardButton>
          </div>
        </InputContainer>
      </TranslationsEditingDisabledDiv>

      <TranslationsEditingDisabledDiv>
        <Checkbox
          label="Hide subpage"
          labelStyle={{display: 'block'}}
          disabled={!canChangeSubpageHiddenness(isSubpageHidden, canHideSubpage)}
          checked={!!isSubpageHidden}
          tooltipContent={
            <Fragment>
              <p>A hidden subpage is inaccessible via links and will not appear in 
              the site menu.</p>
              <p>You cannot hide the home subpage.</p>
              <p>Warning: This is not a security feature—a skilled user may still be able 
              access the data in a hidden subpage.</p>
            </Fragment>
          }
          onChange={(e)=> { onIsSubpageHiddenChange(e.target.checked); }}
        />
      </TranslationsEditingDisabledDiv>

      <TranslationsEditingDisabledDiv>
        <Checkbox
          label="Don't show in navigation"
          labelStyle={{display: 'block'}}
          disabled={!!isSubpageHidden}
          checked={!!isSubpageHidden || !!isMenuItemHidden}
          tooltipContent={
            <Fragment>
              Hides the link from the navigation bar, but the subpage will still be accessible.
            </Fragment>
          }
          onChange={(e)=> { onIsSubpageMenuItemHiddenChange(e.target.checked); }}
        />
      </TranslationsEditingDisabledDiv>

      <TranslationsEditingDisabledDiv>
        <Checkbox
          label="Show content in search results"
          labelStyle={{display: 'block'}}
          checked={canSubpageBeIndexed}
          tooltipContent={
            <Fragment>
              Show all content on this subpage in the in-site search results
            </Fragment>
          }
          onChange={(e)=> { onCanSubpageBeIndexedChange(e.target.checked); }}
        />
      </TranslationsEditingDisabledDiv>

      <TranslationsEditingDisabledDiv>
        <Checkbox
          label="Show scroll to top button"
          labelStyle={{display: 'block'}}
          checked={!!isScrollToTopEnabled}
          tooltipContent={
            <Fragment>
              When enabled, shows a scroll-to-top button if the user scrolls towards the top on a long page.
            </Fragment>
          }
          onChange={(e)=> { onIsScrollToTopEnabledChange(e.target.checked); }}
        />
      </TranslationsEditingDisabledDiv>

      <TranslationsEditingDisabledDiv>
        <Checkbox
          label="Show scrolling progress indicator"
          labelStyle={{ display: 'block' }}
          checked={!!isScrollIndicatorEnabled}
          tooltipContent={
            <Fragment>
              When enabled, shows a scroll indicator for each anchor item in the subpage.
            </Fragment>
          }
          onChange={(e) => { onIsScrollIndicatorEnabledChange(e.target.checked); }}
        />
      </TranslationsEditingDisabledDiv>

      {isScrollIndicatorEnabled &&
        <TranslationsEditingDisabledDiv>
          <InputContainer
            dark
            name="Scroll Indicator Color"
            className="scroll-indicator-color-container"
          >
            <div className="scroll-indicator-color-selector">
              <RadioButtonSet
                selectedValue={scrollIndicatorColor || DEFAULT_SCROLL_INDICATOR}
                onSelectedValueChange={onScrollIndicatorColorChange}
              >
                <RadioButton
                  value={SCROLL_INDICATOR_LIGHT}
                  label="Light"
                />
                <RadioButton
                  value={SCROLL_INDICATOR_DARK}
                  label="Dark"
                />
              </RadioButtonSet>
            </div>
          </InputContainer>
        </TranslationsEditingDisabledDiv>
      }

      {
        !props.isNew &&
          <TranslationsEditingDisabledDiv>
            <SubpageCssClassesInput
              shouldIncludeName={true}
              cssClassesList={cssClassesList}
              tooltip={`
                CSS classes allow you to style this subpage via site scoped CSS or
                custom CSS in the theme. You SHOULD NOT include a "." in front of class names.
              `}
              onCssClassesListChange={onCssClassesListChange}
            />
          </TranslationsEditingDisabledDiv>
      }

    </MenuItemEditorGenerics>
  </span>
);

export default MenuItemSubpage;
